import { useCallback, useEffect, useRef, useState } from 'react'
import { promoChatBus, hasLeftPhone } from './promoChatBus'
import { LEAD_SENT_KEY } from '~/components/Webchat/webchatLead'

// Engaged-user-in-a-pause trigger. All thresholds in one place; ACTIVE_TIME_MS
// is the tuning knob (no A/B infra by design).
const ACTIVE_TIME_MS = 45_000
const MIN_PAGE_AGE_MS = 20_000
const IDLE_MS = 10_000
const PRICE_VIEW_MS = 5_000
const CANVAS_QUIET_MS = 3_000
const CONFIG_SETTLE_MS = 5_000
// Catalog pages have no config/canvas/price signals — sustained scrolling is
// the browse-engagement proxy there. ~2 mobile screens of travel or depth;
// short catalog pages cap max depth, so keep this comfortably reachable.
const SCROLL_ENGAGE_PX = 800
const KEYBOARD_SHRINK_RATIO = 0.75
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000

export const DISMISSED_KEY = 'promo_popup_7d'
const SESSION_SHOWN_KEY = 'promo_popup_shown'

const readLs = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}
const readSs = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function dismissedRecently(): boolean {
  const at = Number(readLs(DISMISSED_KEY)) || 0
  return Date.now() - at < DISMISS_TTL_MS
}

/**
 * Watches engagement + idle signals on a configurator page and flips to true
 * once every show condition holds. Marks the session so it fires at most once.
 *
 * @param configJson serialized current config (null when the page has no
 *   config context) — a change after the settle window counts as engagement.
 * @param scrollEngagement when true, cumulative scrolling (≥1200px) also
 *   counts as engagement — for catalog-style pages without config signals.
 */
export function usePromoPopupTrigger(
  configJson: string | null,
  scrollEngagement = false
): {
  visible: boolean
  hide: () => void
} {
  const [visible, setVisible] = useState(false)

  // Stamped on first effect run (render must stay pure per react-hooks/purity).
  const mountAtRef = useRef(0)
  const activeMsRef = useRef(0)
  const lastActivityRef = useRef(0)
  const lastCanvasRef = useRef(0)
  const canvasDraggingRef = useRef(false)
  const canvasTouchedRef = useRef(false)
  const configChangedRef = useRef(false)
  const configSnapshotRef = useRef<string | null>(null)
  const priceViewMsRef = useRef(0)
  const priceVisibleSinceRef = useRef<number | null>(null)
  const keyboardOpenRef = useRef(false)
  const scrollDistanceRef = useRef(0)
  const maxScrollYRef = useRef(0)
  const lastScrollYRef = useRef<number | null>(null)

  // Config-change engagement. The shell may normalize the config right after
  // mount (backend constraints arriving, etc.) — snapshot keeps following the
  // value through the settle window so only later, user-driven edits count.
  useEffect(() => {
    if (configJson === null) return
    if (!mountAtRef.current) mountAtRef.current = Date.now()
    if (Date.now() - mountAtRef.current < CONFIG_SETTLE_MS) {
      configSnapshotRef.current = configJson
      return
    }
    if (
      configSnapshotRef.current !== null &&
      configJson !== configSnapshotRef.current
    ) {
      configChangedRef.current = true
    }
    configSnapshotRef.current ??= configJson
  }, [configJson])

  useEffect(() => {
    if (!mountAtRef.current) mountAtRef.current = Date.now()
    lastActivityRef.current = Date.now()

    const isCanvasTarget = (t: EventTarget | null): boolean =>
      t instanceof Element && !!t.closest('canvas')

    const onActivity = (e: Event) => {
      lastActivityRef.current = Date.now()
      if (isCanvasTarget(e.target)) {
        canvasTouchedRef.current = true
        lastCanvasRef.current = Date.now()
      }
      if (e.type === 'scroll') {
        const y = window.scrollY
        if (lastScrollYRef.current !== null) {
          scrollDistanceRef.current += Math.abs(y - lastScrollYRef.current)
        }
        lastScrollYRef.current = y
        // Coalesced events undercount the travelled distance — depth reached
        // is the jump-proof second signal.
        maxScrollYRef.current = Math.max(maxScrollYRef.current, y)
      }
    }
    const onPointerDown = (e: PointerEvent) => {
      onActivity(e)
      if (isCanvasTarget(e.target)) canvasDraggingRef.current = true
    }
    const onPointerMove = () => {
      if (canvasDraggingRef.current) {
        lastActivityRef.current = Date.now()
        lastCanvasRef.current = Date.now()
      }
    }
    const onPointerUp = () => {
      if (canvasDraggingRef.current) {
        canvasDraggingRef.current = false
        lastCanvasRef.current = Date.now()
      }
    }

    const opts: AddEventListenerOptions = { capture: true, passive: true }
    window.addEventListener('pointerdown', onPointerDown, opts)
    window.addEventListener('pointermove', onPointerMove, opts)
    window.addEventListener('pointerup', onPointerUp, opts)
    window.addEventListener('pointercancel', onPointerUp, opts)
    window.addEventListener('touchstart', onActivity, opts)
    window.addEventListener('click', onActivity, opts)
    window.addEventListener('keydown', onActivity, opts)
    window.addEventListener('scroll', onActivity, opts)
    window.addEventListener('wheel', onActivity, opts)

    // On-screen keyboard heuristic: the visual viewport shrinks well below the
    // largest height seen (address-bar collapse never cuts 25%). A width jump
    // means rotation — rebase the height there, or landscape would read as
    // "keyboard open" for the rest of the session.
    const vv = window.visualViewport
    let maxViewportH = vv?.height ?? window.innerHeight
    let lastViewportW = vv?.width ?? window.innerWidth
    const onViewportResize = () => {
      if (!vv) return
      if (Math.abs(vv.width - lastViewportW) > 50) {
        maxViewportH = vv.height
        lastViewportW = vv.width
      } else {
        maxViewportH = Math.max(maxViewportH, vv.height)
      }
      keyboardOpenRef.current = vv.height < maxViewportH * KEYBOARD_SHRINK_RATIO
    }
    vv?.addEventListener('resize', onViewportResize)

    // Price-block dwell time. The block renders lazily, so the tick below
    // (re)queries until it appears.
    let priceObserver: IntersectionObserver | null = null
    let observedPriceEl: Element | null = null
    const observePrice = () => {
      if (observedPriceEl?.isConnected) return
      const el = document.querySelector('[data-price-block]')
      if (!el) return
      priceObserver?.disconnect()
      priceObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1]
          const now = Date.now()
          if (entry.isIntersecting) {
            priceVisibleSinceRef.current ??= now
          } else if (priceVisibleSinceRef.current !== null) {
            priceViewMsRef.current += now - priceVisibleSinceRef.current
            priceVisibleSinceRef.current = null
          }
        },
        { threshold: 0.5 }
      )
      priceObserver.observe(el)
      observedPriceEl = el
    }

    const priceViewMs = () =>
      priceViewMsRef.current +
      (priceVisibleSinceRef.current !== null
        ? Date.now() - priceVisibleSinceRef.current
        : 0)

    let id = 0
    const stop = () => window.clearInterval(id)

    id = window.setInterval(() => {
      const now = Date.now()
      const pageVisible = document.visibilityState === 'visible'
      if (pageVisible) activeMsRef.current += 1000
      observePrice()

      if (import.meta.env.DEV) {
        ;(window as unknown as Record<string, unknown>).__promoDebug = {
          ageMs: now - mountAtRef.current,
          activeMs: activeMsRef.current,
          idleMs: now - lastActivityRef.current,
          engaged: {
            config: configChangedRef.current,
            canvas: canvasTouchedRef.current,
            priceMs: priceViewMs(),
            scrollPx: scrollDistanceRef.current,
            maxScrollY: maxScrollYRef.current,
            scrollMode: scrollEngagement,
          },
          keyboard: keyboardOpenRef.current,
          panelOpen: promoChatBus.state.panelOpen,
          teaser: promoChatBus.state.teaserVisible,
        }
      }

      // Hard stops — this session/visitor is out of scope.
      if (readSs(SESSION_SHOWN_KEY)) return stop()
      if (dismissedRecently()) return stop()
      if (hasLeftPhone()) return stop()
      if (readSs(LEAD_SENT_KEY)) return stop()

      if (!pageVisible) return
      // Deliberately NOT gated on chat history: the token (and thus old
      // messages) lives in localStorage, so "has messages ever" would silence
      // the popup for every returning visitor. Open panel / phone left /
      // lead-this-session are the real signals.
      if (promoChatBus.state.panelOpen) return
      if (promoChatBus.state.teaserVisible) return
      if (keyboardOpenRef.current) return
      if (now - mountAtRef.current < MIN_PAGE_AGE_MS) return
      if (activeMsRef.current < ACTIVE_TIME_MS) return

      const engaged =
        configChangedRef.current ||
        canvasTouchedRef.current ||
        priceViewMs() >= PRICE_VIEW_MS ||
        (scrollEngagement &&
          Math.max(scrollDistanceRef.current, maxScrollYRef.current) >=
            SCROLL_ENGAGE_PX)
      if (!engaged) return

      if (now - lastActivityRef.current < IDLE_MS) return
      if (canvasDraggingRef.current) return
      if (
        lastCanvasRef.current &&
        now - lastCanvasRef.current < CANVAS_QUIET_MS
      )
        return

      stop()
      try {
        sessionStorage.setItem(SESSION_SHOWN_KEY, '1')
      } catch {
        /* ignore */
      }
      // Synchronously, not in the render effect: the teaser timer checks this
      // flag and must not race the one-effect-tick lag into a stacked UI.
      promoChatBus.state.popupVisible = true
      setVisible(true)
    }, 1000)

    return () => {
      stop()
      window.removeEventListener('pointerdown', onPointerDown, opts)
      window.removeEventListener('pointermove', onPointerMove, opts)
      window.removeEventListener('pointerup', onPointerUp, opts)
      window.removeEventListener('pointercancel', onPointerUp, opts)
      window.removeEventListener('touchstart', onActivity, opts)
      window.removeEventListener('click', onActivity, opts)
      window.removeEventListener('keydown', onActivity, opts)
      window.removeEventListener('scroll', onActivity, opts)
      window.removeEventListener('wheel', onActivity, opts)
      vv?.removeEventListener('resize', onViewportResize)
      priceObserver?.disconnect()
    }
  }, [scrollEngagement])

  const hide = useCallback(() => setVisible(false), [])
  return { visible, hide }
}
